import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, Button, Input, Select, Textarea } from '@/components/ui';
import { ArrowLeft, Save, Calendar, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface Event {
  id: string;
  title: string;
  titleTr?: string;
  slug: string;
  description?: string;
  descriptionTr?: string;
  imageUrl?: string;
  eventDate: string;
  endDate?: string;
  ticketLink?: string;
  status: string;
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

export function EventEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [formData, setFormData] = useState<Partial<Event>>({
    title: '',
    titleTr: '',
    slug: '',
    description: '',
    descriptionTr: '',
    imageUrl: '',
    eventDate: '',
    endDate: '',
    ticketLink: '',
    status: 'draft',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: event, isLoading } = useQuery<Event>({
    queryKey: ['event', id],
    queryFn: async () => {
      const response = await api.get(`/admin/events/${id}`);
      return response.data?.data;
    },
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (event) {
      setFormData({
        ...event,
        eventDate: event.eventDate ? format(new Date(event.eventDate), "yyyy-MM-dd'T'HH:mm") : '',
        endDate: event.endDate ? format(new Date(event.endDate), "yyyy-MM-dd'T'HH:mm") : '',
      });
    }
  }, [event]);

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Event>) => {
      if (isNew) {
        return api.post('/admin/events', data);
      }
      return api.put(`/admin/events/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/events');
    },
    onError: (error: Error & { response?: { data?: { errors?: Record<string, string> } } }) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  function handleChange(field: keyof Event, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function generateSlug(title: string) {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(formData);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/events"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isNew ? 'Add Event' : `Edit: ${event?.title}`}
            </h1>
            <p className="text-gray-400 mt-1">
              {isNew ? 'Create a new event' : 'Update event details'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/events')}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {isNew ? 'Create Event' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card padding="lg">
            <CardHeader title="Event Details" />
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Title (English)"
                  value={formData.title}
                  onChange={(e) => {
                    handleChange('title', e.target.value);
                    if (isNew && !formData.slug) {
                      handleChange('slug', generateSlug(e.target.value));
                    }
                  }}
                  error={errors.title}
                  required
                />
                <Input
                  label="Title (Turkish)"
                  value={formData.titleTr}
                  onChange={(e) => handleChange('titleTr', e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <Input
                label="URL Slug"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                error={errors.slug}
                required
              />

              <Textarea
                label="Description (English)"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
              />

              <Textarea
                label="Description (Turkish)"
                value={formData.descriptionTr}
                onChange={(e) => handleChange('descriptionTr', e.target.value)}
                rows={4}
                placeholder="Optional"
              />
            </div>
          </Card>

          <Card padding="lg">
            <CardHeader title="Date & Time" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Event Date & Time"
                type="datetime-local"
                value={formData.eventDate}
                onChange={(e) => handleChange('eventDate', e.target.value)}
                icon={<Calendar className="h-4 w-4" />}
                required
              />
              <Input
                label="End Date & Time"
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                icon={<Calendar className="h-4 w-4" />}
                helper="Optional"
              />
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card padding="lg">
            <CardHeader title="Image Preview" />
            <div className="aspect-video rounded-lg overflow-hidden bg-midnight-100 mb-4">
              {formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="Event preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                  <Calendar className="h-12 w-12 mb-2" />
                  <span className="text-sm">No image</span>
                </div>
              )}
            </div>
            <Input
              label="Image URL"
              value={formData.imageUrl}
              onChange={(e) => handleChange('imageUrl', e.target.value)}
              placeholder="https://..."
            />
          </Card>

          <Card padding="lg">
            <CardHeader title="Publishing" />
            <div className="space-y-4">
              <Select
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              />
              <Input
                label="Ticket Link"
                value={formData.ticketLink}
                onChange={(e) => handleChange('ticketLink', e.target.value)}
                placeholder="https://..."
                helper="External ticket purchase URL"
              />
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}
